import { WithDisposable } from '@blocksuite/block-std';
import type { EdgelessRootBlockComponent } from '@blocksuite/blocks';
import { actionItemSelectedSlot, attendeeActionsPanelOpenedSlot } from '@pique/core/components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots';
import { ACTION_ITEM_STATUS, type SerializedAttendee, type SerializedTodoItem } from '@pique/core/modules/properties/services/schema';
import { css, html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { AIStarIcon } from '../../_common/icons';

@customElement('edgeless-attendee-actions-panel')
export class EdgelessAttendeeActionsPanel extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      min-width: 280px;
      max-width: 400px;
      max-height: 400px;
      overflow: hidden;
      border-radius: 12px;
      height: 180px;
    }

    .panel-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3px;
      border-bottom: 1px solid var(--affine-border-color);
      background: linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%);
      flex-shrink: 0;
    }

    .header-message {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1px;
      text-align: center;
    }

    .header-title {
      font-size: 14px;
      font-weight: 600;
      color: var(--affine-text-primary-color);
      line-height: 1.3;
    }

    .header-subtitle {
      font-size: 12px;
      font-weight: 500;
      color: var(--affine-text-secondary-color);
      line-height: 1.2;
      opacity: 0.8;
    }

    .header-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      opacity: 0.7;
    }

    .action-items-section {
      flex: 1;
      overflow-y: auto;
      padding: 4px;
      min-height: 0;
    }

    .action-items-section::-webkit-scrollbar {
      width: 6px;
    }

    .action-items-section::-webkit-scrollbar-track {
      background: transparent;
    }

    .action-items-section::-webkit-scrollbar-thumb {
      background: var(--affine-border-color);
      border-radius: 3px;
    }

    .action-items-section::-webkit-scrollbar-thumb:hover {
      background: var(--affine-text-secondary-color);
    }

    .action-item {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 6px;
      border-radius: 8px;
      margin-bottom: 6px;
      transition: all 0.2s ease;
      cursor: pointer;
      border: 1px solid transparent;
      position: relative;
      background: var(--affine-background-secondary-color);
    }

    .action-item:hover {
      background-color: var(--affine-hover-color);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .action-item.completed {
      opacity: 0.7;
      background: var(--affine-background-tertiary-color);
    }

    .action-item.completed .action-title {
      text-decoration: line-through;
      color: var(--affine-text-secondary-color);
    }

    .action-checkbox {
      width: 16px;
      height: 16px;
      border: 2px solid var(--affine-border-color);
      border-radius: 3px;
      margin-top: 2px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      font-size: 10px;
    }

    .action-checkbox.completed {
      background-color: var(--affine-primary-color);
      border-color: var(--affine-primary-color);
      color: white;
    }

    .action-content {
      flex: 1;
      min-width: 0;
    }

    .action-title {
      font-size: 13px;
      font-weight: 500;
      color: var(--affine-text-primary-color);
      line-height: 1.4;
      margin-bottom: 2px;
    }

    .action-meta {
      font-size: 11px;
      color: var(--affine-text-secondary-color);
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .progress-badge {
      padding: 2px 4px;
      border-radius: 12px;
      font-size: 10px;
      font-weight: 600;
      background-color: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .progress-bar {
      width: 24px;
      height: 3px;
      background-color: rgba(59, 130, 246, 0.2);
      border-radius: 2px;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      background-color: #3b82f6;
      border-radius: 2px;
      transition: width 0.3s ease;
    }

    .status-badge {
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 10px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .status-badge.completed {
      background-color: rgba(34, 197, 94, 0.1);
      color: #22c55e;
    }

    .status-badge.pending {
      background-color: rgba(249, 115, 22, 0.1);
      color: #f97316;
    }

    .due-date {
      font-size: 11px;
      color: var(--affine-text-secondary-color);
      font-weight: 500;
    }

    .empty-state {
      text-align: center;
      padding: 20px 12px;
      color: var(--affine-text-secondary-color);
    }

    .empty-icon {
      font-size: 32px;
      margin-bottom: 8px;
      opacity: 0.5;
    }

    .empty-text {
      font-size: 13px;
      line-height: 1.4;
    }

    .action-item.selected {
      background-color: rgba(139, 92, 246, 0.1);
      border: 1px solid var(--affine-primary-color);
      border-radius: 8px;
      position: relative;
    }

    .action-item.selected::before {
      content: '';
      position: absolute;
      top: 8px;
      right: 8px;
      width: 16px;
      height: 16px;
      background: var(--affine-primary-color);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .action-item.selected::after {
      content: '✓';
      position: absolute;
      top: 8px;
      right: 8px;
      width: 16px;
      height: 16px;
      color: white;
      font-size: 10px;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }
  `;

  @state()
  accessor _selectedItems = new Set<string>();

  private _selectItem(item: SerializedTodoItem, event: Event) {
    event.stopPropagation();
    const newSelectedItems = new Set(this._selectedItems);
    if (newSelectedItems.has(item.id)) {
      newSelectedItems.delete(item.id);
    } else {
      newSelectedItems.add(item.id);
    }
    this._selectedItems = newSelectedItems;

    // Emit slot with the specific selected action item
    actionItemSelectedSlot.emit({
      attendee: this.attendee,
      actionItem: item
    });

    // Force a re-render by requesting an update
    this.requestUpdate();
  }

  override render() {
    if (!this.attendee) return nothing;

    const items = this.actionItems;
    const pendingItems = items.filter(item => item.status !== ACTION_ITEM_STATUS.DONE);
    const completedItems = items.filter(item => item.status === ACTION_ITEM_STATUS.DONE);

    // Show pending items first, then completed items
    const sortedItems = [...pendingItems, ...completedItems];

    return html`
      <div class="panel-header">
        <div class="header-message">
          <div class="header-title">Things to follow up on</div>
          <div class="header-subtitle">Add to today's agenda</div>
        </div>
      </div>

      ${items.length > 0 ? html`
        <div class="action-items-section">
          ${repeat(
      sortedItems,
      item => item.id,
      item => html`
              <div class="action-item ${this._selectedItems.has(item.id) ? 'selected' : ''} ${item.status === ACTION_ITEM_STATUS.DONE ? 'completed' : ''}" @click=${(e: Event) => this._selectItem(item, e)}>
                <div class="action-content">
                  <div class="action-title">${item.title}</div>
                  <div class="action-meta">
                    ${typeof item.progress === 'number' ? html`
                      <span class="progress-badge">
                        <div class="progress-bar">
                          <div class="progress-fill" style="width: ${item.progress}%"></div>
                        </div>
                        ${item.progress}%
                      </span>
                    ` : nothing}
                    ${item.dueDate ? html`
                      <span class="due-date">Due: ${new Date(item.dueDate).toLocaleDateString()}</span>
                    ` : nothing}
                    ${item.status === ACTION_ITEM_STATUS.DONE ? html`
                      <span class="status-badge completed">${ACTION_ITEM_STATUS.DONE}</span>
                    ` : html`
                      <span class="status-badge pending">Pending</span>
                    `}
                  </div>
                </div>
              </div>
            `
    )}
        </div>
      ` : html`
        <div class="empty-state">
          <div class="empty-text">
            No action items yet.
          </div>
        </div>
      `}
    `;
  }

  @property({ attribute: false })
  accessor attendee!: SerializedAttendee;

  @property({ attribute: false })
  accessor actionItems: SerializedTodoItem[] = [];

  @property({ attribute: false })
  accessor onToggleAction: ((item: SerializedTodoItem, newStatus: string) => void) | undefined = undefined;

  @property({ attribute: false })
  accessor onCreateAction: ((attendee: SerializedAttendee) => void) | undefined = undefined;

  @property({ attribute: false })
  accessor onAskAI: ((attendee: SerializedAttendee, actionItems: SerializedTodoItem[]) => void) | undefined = undefined;
}

@customElement('edgeless-attendee-actions-button')
export class EdgelessAttendeeActionsButton extends WithDisposable(LitElement) {
  override firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties);

    // Find the editor-menu-button and add custom styles
    const menuButton = this.shadowRoot?.querySelector('editor-menu-button');
    if (menuButton) {
      // Add a mutation observer to style the menu content when it appears
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-show') {
            const target = mutation.target as HTMLElement;
            const isShowing = Object.hasOwn(target.dataset, 'show');

            if (isShowing) {
              attendeeActionsPanelOpenedSlot.emit();
            }
          }
        });

        const menuContent = menuButton.shadowRoot?.querySelector('editor-menu-content');
        if (menuContent) {
          // Apply custom positioning and styling
          menuContent.style.borderRadius = '12px';
          menuContent.style.marginLeft = '42px';
          menuContent.style.marginTop = '4px';
        }
      });

      if (menuButton.shadowRoot) {
        observer.observe(menuButton.shadowRoot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-show']
        });
      }

      this._disposables.add(() => observer.disconnect());
    }
  }
  private readonly _handleToggleAction = (_item: SerializedTodoItem, _newStatus: string) => {
    // TODO: Update action item status in properties
  };

  private readonly _handleCreateAction = (_attendee: SerializedAttendee) => {
    // TODO: Create new action item
  };

  private readonly _handleAskAI = (_attendee: SerializedAttendee, _actionItems: SerializedTodoItem[]) => {
    // TODO: Trigger AI assistant for this attendee
  };

  override render() {
    return html`
      <editor-menu-button
        .contentPadding=${'8px'}
        .button=${html`
          <editor-icon-button
            aria-label="Attendee Actions"
            .tooltip=${this.attendee.name}
            style="
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 6px 12px;
              border-radius: 8px;
              transition: all 0.2s ease;
            "
          >
            <div style="
              display: flex;
              align-items: center;
              gap: 8px;
            ">
              <!-- Avatar -->
              <div style="
                width: 20px; 
                height: 20px; 
                border-radius: 50%; 
                background: ${this.attendee.avatar ? `url(${this.attendee.avatar})` : '#4285f4'};
                background-size: cover;
                background-position: center;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 10px;
                font-weight: bold;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
              ">
                ${this.attendee.avatar ? '' : (this.attendee.name?.[0] || this.attendee.email?.[0] || '?').toUpperCase()}
              </div>
              
              <!-- Email/Name -->
              <span style="
                font-size: 12px;
                color: var(--affine-text-primary-color);
                font-weight: 500;
                max-width: 120px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              ">${this.attendee.name || this.attendee.email}</span>
              
              <!-- AI Star Icon -->
              <div style="
                display: flex;
                align-items: center;
                justify-content: center;
                width: 20px;
                height: 20px;
                margin-left: 4px;
              ">
                ${AIStarIcon}
              </div>
            </div>
          </editor-icon-button>
        `}
      >
        <edgeless-attendee-actions-panel
          class="custom"
          .attendee=${this.attendee}
          .actionItems=${this.actionItems}
          .onToggleAction=${this._handleToggleAction}
          .onCreateAction=${this._handleCreateAction}
          .onAskAI=${this._handleAskAI}
        >
        </edgeless-attendee-actions-panel>
      </editor-menu-button>
    `;
  }

  @property({ attribute: false })
  accessor attendee!: SerializedAttendee;

  @property({ attribute: false })
  accessor actionItems: SerializedTodoItem[] = [];

  @property({ attribute: false })
  accessor edgeless!: EdgelessRootBlockComponent;
}

export function renderAttendeeActionsButton(
  attendee: SerializedAttendee,
  actionItems: SerializedTodoItem[],
  edgeless: EdgelessRootBlockComponent
) {
  if (!attendee) return nothing;

  return html`
    <edgeless-attendee-actions-button
      .attendee=${attendee}
      .actionItems=${actionItems}
      .edgeless=${edgeless}
    >
    </edgeless-attendee-actions-button>
  `;
}

declare global {
  interface HTMLElementTagNameMap {
    'edgeless-attendee-actions-panel': EdgelessAttendeeActionsPanel;
    'edgeless-attendee-actions-button': EdgelessAttendeeActionsButton;
  }
} 